import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { FadeInView } from '@/components/animations';
import { translations } from '@/i18n/translations';

/**
 * FAQ 项接口
 */
interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

/**
 * FAQSection 组件属性
 */
interface FAQSectionProps {
  className?: string;
}

/**
 * FAQ 区域组件
 * 
 * 展示常见问题，使用手风琴式折叠面板
 * 支持多个问题同时展开
 */
export function FAQSection({ className = '' }: FAQSectionProps) {
  const { t, language } = useLanguage();
  
  // 使用 Set 存储已展开的问题 ID，支持多个问题同时展开
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  // 从翻译数据中获取 FAQ 项
  const faqItemsData = translations[language].faq.items;
  const faqItems: FAQItem[] = faqItemsData.map((item, index) => ({
    id: `faq-${index}`,
    question: item.question,
    answer: item.answer,
  }));

  /**
   * 切换问题的展开/折叠状态
   */
  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id); // 如果已展开，则折叠
      } else {
        newSet.add(id); // 如果已折叠，则展开
      }
      return newSet;
    });
  };

  return (
    <section
      className={`py-20 px-4 bg-gray-50 ${className}`}
    >
      <div className="max-w-4xl mx-auto">
        {/* 标题 */}
        <FadeInView>
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            {t('faq.title')}
          </h2>
        </FadeInView>

        {/* FAQ 列表 */}
        <div className="space-y-4">
          {faqItems.map((item, index) => {
            const isOpen = openItems.has(item.id);
            
            return (
              <FadeInView key={item.id} delay={index * 50}>
                <div
                  className="bg-white rounded-lg shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md"
                >
                  {/* 问题按钮 */}
                  <button
                    onClick={() => toggleItem(item.id)}
                    aria-expanded={isOpen}
                    className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                  >
                    <span className="text-lg font-semibold text-gray-900 flex-1">
                      {item.question}
                    </span>
                    
                    {/* 展开/折叠图标 */}
                    <svg
                      className={`w-6 h-6 text-primary transition-transform duration-200 flex-shrink-0 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* 答案区域 */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isOpen ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              </FadeInView>
            );
          })}
        </div>
      </div>
    </section>
  );
}
